/**
 * useVerificationFlow Hook
 *
 * Manages the verification state machine for email recipients.
 * Handles state transitions, code validation, and resend cooldowns.
 *
 * State Machine:
 *   idle ──────────────> sending ──────────────> awaiting_code
 *     │                     │                        │ │
 *     │                     └─ (failure) ─> idle     │ │
 *     │                                              │ │
 *     │                     ┌─────────────────────────┘ │
 *     │                     │                           │
 *     │                     v                           │
 *     │                  verifying ────> error ────────>│
 *     │                     │                           │
 *     │                     │                           │
 *     │                     v                           │
 *     │                  verified <─────────────────────┘
 *     │                     │
 *     │                     v
 *     └─────────────────> testing ──────> verified
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { VerificationState, EmailRecipient } from '../types';
import {
  VERIFICATION_CODE_LENGTH,
  RESEND_COOLDOWN_SECONDS,
  ANIMATION_DURATIONS,
} from '../constants';
import { formatVerificationCodeInput } from '../utils';

interface UseVerificationFlowOptions {
  /** The recipient being verified */
  recipient: EmailRecipient;
  /** Handler to send verification email */
  onSendVerification: (recipient: EmailRecipient) => Promise<boolean>;
  /** Handler to verify the code */
  onVerifyCode: (recipient: EmailRecipient, code: string) => Promise<boolean>;
  /** Handler to send test email */
  onTest: (recipient: EmailRecipient) => Promise<boolean>;
  /** Optional callback when verification succeeds */
  onVerificationSuccess?: () => void;
  /** Auto-submit when code length is reached */
  autoSubmit?: boolean;
  /** Delay before auto-submit (ms) */
  autoSubmitDelay?: number;
}

export interface UseVerificationFlowReturn {
  // State
  state: VerificationState;
  code: string;
  canResend: boolean;
  resendCountdown: number;
  /** Whether the recipient was just verified (for animations) */
  justVerified: boolean;
  /** Whether the code input is visible */
  isCodeInputVisible: boolean;
  /** Whether the code input is animating out */
  isCodeInputExiting: boolean;
  // Actions
  sendVerification: () => Promise<void>;
  submitCode: () => Promise<void>;
  resendCode: () => Promise<void>;
  setCode: (code: string) => void;
  sendTestEmail: () => Promise<void>;
  reset: () => void;
}

export function useVerificationFlow({
  recipient,
  onSendVerification,
  onVerifyCode,
  onTest,
  onVerificationSuccess,
  autoSubmit = true,
  autoSubmitDelay = 300,
}: UseVerificationFlowOptions): UseVerificationFlowReturn {
  // Core state
  const [state, setState] = useState<VerificationState>(
    recipient.is_verified ? 'verified' : 'idle'
  );
  const [code, setCodeInternal] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Animation state
  const [justVerified, setJustVerified] = useState(false);
  const [isCodeInputVisible, setIsCodeInputVisible] = useState(false);
  const [isCodeInputExiting, setIsCodeInputExiting] = useState(false);

  // Refs for cleanup
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const justVerifiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const codeInputExitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync state with recipient changes
  useEffect(() => {
    if (recipient.is_verified && state !== 'verified' && state !== 'testing') {
      setState('verified');
    }
  }, [recipient.is_verified, state]);

  // Manage code input visibility based on state
  useEffect(() => {
    const isInCodeState =
      state === 'awaiting_code' || state === 'error' || state === 'verifying';

    if (isInCodeState && !isCodeInputVisible) {
      // Entering code state - show input with animation
      setIsCodeInputExiting(false);
      setIsCodeInputVisible(true);
    } else if (!isInCodeState && isCodeInputVisible) {
      // Exiting code state - animate out then hide
      setIsCodeInputExiting(true);

      if (codeInputExitTimeoutRef.current) {
        clearTimeout(codeInputExitTimeoutRef.current);
      }

      codeInputExitTimeoutRef.current = setTimeout(() => {
        setIsCodeInputVisible(false);
        setIsCodeInputExiting(false);
      }, ANIMATION_DURATIONS.CODE_INPUT_EXIT);
    }
  }, [state, isCodeInputVisible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
      }
      if (justVerifiedTimeoutRef.current) {
        clearTimeout(justVerifiedTimeoutRef.current);
      }
      if (codeInputExitTimeoutRef.current) {
        clearTimeout(codeInputExitTimeoutRef.current);
      }
    };
  }, []);

  // Start resend cooldown
  const startResendCooldown = useCallback(() => {
    setCanResend(false);
    setResendCountdown(RESEND_COOLDOWN_SECONDS);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Trigger just verified animation
  const triggerJustVerifiedAnimation = useCallback(() => {
    setJustVerified(true);

    if (justVerifiedTimeoutRef.current) {
      clearTimeout(justVerifiedTimeoutRef.current);
    }

    justVerifiedTimeoutRef.current = setTimeout(() => {
      setJustVerified(false);
    }, ANIMATION_DURATIONS.JUST_VERIFIED);
  }, []);

  // Send verification email
  const sendVerification = useCallback(async () => {
    setState('sending');
    const success = await onSendVerification(recipient);

    if (success) {
      setState('awaiting_code');
      setCodeInternal('');
      startResendCooldown();
    } else {
      setState('idle');
    }
  }, [recipient, onSendVerification, startResendCooldown]);

  // Submit verification code
  const submitCode = useCallback(async () => {
    if (code.length !== VERIFICATION_CODE_LENGTH) return;

    setState('verifying');
    const success = await onVerifyCode(recipient, code);

    if (success) {
      setState('verified');
      triggerJustVerifiedAnimation();
      onVerificationSuccess?.();
    } else {
      setState('error');
    }
  }, [recipient, code, onVerifyCode, onVerificationSuccess, triggerJustVerifiedAnimation]);

  // Resend verification code
  const resendCode = useCallback(async () => {
    if (!canResend) return;

    setState('sending');
    const success = await onSendVerification(recipient);

    if (success) {
      setState('awaiting_code');
      setCodeInternal('');
      startResendCooldown();
    } else {
      // Stay in awaiting_code on resend failure
      setState('awaiting_code');
    }
  }, [recipient, onSendVerification, canResend, startResendCooldown]);

  // Set verification code with formatting and error clearing
  const setCode = useCallback(
    (value: string) => {
      const formatted = formatVerificationCodeInput(value);
      setCodeInternal(formatted);

      // Clear error state when user starts typing
      if (state === 'error') {
        setState('awaiting_code');
      }
    },
    [state]
  );

  // Auto-submit when code is complete
  useEffect(() => {
    if (
      autoSubmit &&
      code.length === VERIFICATION_CODE_LENGTH &&
      state === 'awaiting_code'
    ) {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
      }

      autoSubmitTimeoutRef.current = setTimeout(() => {
        submitCode();
      }, autoSubmitDelay);

      return () => {
        if (autoSubmitTimeoutRef.current) {
          clearTimeout(autoSubmitTimeoutRef.current);
        }
      };
    }
  }, [autoSubmit, code, state, autoSubmitDelay, submitCode]);

  // Send test email
  const sendTestEmail = useCallback(async () => {
    setState('testing');
    await onTest(recipient);
    setState('verified');
  }, [recipient, onTest]);

  // Reset to initial state
  const reset = useCallback(() => {
    setState(recipient.is_verified ? 'verified' : 'idle');
    setCodeInternal('');
    setCanResend(true);
    setResendCountdown(0);
    setJustVerified(false);
    setIsCodeInputVisible(false);
    setIsCodeInputExiting(false);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
    if (justVerifiedTimeoutRef.current) {
      clearTimeout(justVerifiedTimeoutRef.current);
      justVerifiedTimeoutRef.current = null;
    }
    if (codeInputExitTimeoutRef.current) {
      clearTimeout(codeInputExitTimeoutRef.current);
      codeInputExitTimeoutRef.current = null;
    }
  }, [recipient.is_verified]);

  return {
    // State
    state,
    code,
    canResend,
    resendCountdown,
    justVerified,
    isCodeInputVisible,
    isCodeInputExiting,
    // Actions
    sendVerification,
    submitCode,
    resendCode,
    setCode,
    sendTestEmail,
    reset,
  };
}
